<template>
<div class="container">
	<div class="page page-inventory">
		<!-- Header -->
		<div class="page-header">
			<h1 class="page-title mb-3">{{ $t(pageTitle) }}</h1>
		</div>

		<div class="d-lg-flex align-items-center mb-3">
			<!-- Table Filters -->
			<div class="table-filters d-flex align-items-center ml-a">
				<!-- Date Filter -->
				<daterangepicker
					:presets="filterDatePresets"
					:start="since"
					:end="until"
					@selected="setFilterDate">
				</daterangepicker>
			</div>
		</div>

		<!-- Main Table -->
		<div class="wallet-history-table table-responsive">
			<table class="table table-primary table-striped table-hover">
				<thead>
					<tr>
						<th width="15%" class="text-left">
							<div class="d-inline-flex align-items-center justify-content-center">
								<span>{{ titleId }}</span>
							</div>
						</th>
						<th>Value</th>
						<th width="30%">
							<div class="d-inline-flex align-items-center justify-content-center">
								<span>Status</span>
								<i class="material-icons ml-2" v-tooltip="'Pending: Belum masuk ke credits balance<br>Completed: Sudah masuk ke credits balance'">info</i>
							</div>
						</th>
						<th width="15%">Paid Date</th>
						<th width="15%">Due Date</th>
						<th width="10%"></th>
					</tr>
				</thead>
				<tbody>
					<template
						v-for="(history, index) in histories">
						<tr
							class="cur-p"
							:class="{ 'highlighted': selected && selected._id === history._id }"
							:key="`${index}-1`"
							@click="showDetails(history)">

							<!-- Order ID -->
							<td>
								<span class="text-bold">#{{ getValueId(history) }}</span>
							</td>

							<!-- Value -->
							<td class="text-right">
								<span class="text-success text-bold">{{ formatNumber(history.value) }}</span>
							</td>

							<!-- Status -->
							<td class="text-center">
								<mark :class="`order-status status-${history.status}`"><span>{{ history.status }}</span></mark>
							</td>

							<!-- Date -->
							<td class="text-center">
								<span>{{ formatDate(history.created_at) }}</span>
							</td>

							<!-- Due Date -->
							<td class="text-center">
								<span>{{ formatDate(history.due_date) }}</span>
							</td>

							<!-- Actions -->
							<td class="text-center">
								<div class="d-flex align-items-center justify-content-end pr-4">
									<i class="material-icons cur-p" v-tooltip="'Show Details'">
										<template v-if="selected && selected._id === history._id">keyboard_arrow_up</template>
										<template v-else>keyboard_arrow_down</template>
									</i>
								</div>
							</td>
						</tr>
						<tr
							class="detail-view"
							:key="`${index}-2`"
							v-if="selected && selected._id === history._id">
							<td colspan="6">
								<template v-if="history.details">
									<div
										class="text-center"
										v-if="history.details.length === 0">
										- No Transaction Details -
									</div>
									<!-- Details -->
									<div class="p-3" v-else>
										<h5 class="mb-3">Transaction Details</h5>
										<stash-details :details="history.details" />
									</div>
								</template>
								<div class="is-loading dark" v-if="!history.details"></div>
							</td>
						</tr>
					</template>
				</tbody>
			</table>
		</div>

		<!-- Table Loader -->
		<div
			class="table-fetch-status"
			v-if="isFetching">
			<div class="is-loading large dark m-auto"></div>
		</div>

		<!-- Table Empty -->
		<div
			class="list-empty"
			v-if="histories.length === 0 && isFetching === false">
			<span><i class="material-icons md-48 mr-2">error_outline</i></span>
			<span v-if="keyword === ''">There is no transactions in this date range.</span>
			<span v-else>No transactions was found</span>
		</div>
	</div>
</div>
</template>

<script>
import { mapGetters } from 'vuex';
import dayjs from 'dayjs';
import Daterangepicker from '@/components/Daterangepicker.vue';
import StashDetails from '@/components/StashDetails.vue';
import {
	duplicateVar,
	getDateRange,
	formatNumberPlain,
} from '@/lib/helper';
import walletStashApi from '@/api/wallet-stash';

const defaultFilterDate = getDateRange();

export default {
	name: 'WalletStash',
	components: {
		Daterangepicker,
		StashDetails,
	},
	data() {
		return {
			keyword: '',
			limit: 25,
			page: 1,
			sort_by: '_id',
			sort: 'desc',
			histories: [],
			selected: null,
			isFetching: false,
			isAll: false,
			filterDate: defaultFilterDate,
			currency: 'Rp',
		};
	},
	methods: {
		resetHistories() {
			this.histories = [];
			this.isAll = false;
			this.page = 1;
		},
		fetchHistories(reset = true) {
			this.isFetching = true;
			if (reset) {
				this.resetHistories();
			}
			const params = this.fetchParams;
			const callback = (response) => {
				const histories = response.data;
				histories.forEach((history) => {
					this.histories.push(history);
				});
				this.page++;
				if (histories.length < this.limit) {
					this.isAll = true;
				}
				this.isFetching = false;
			};
			const errorCallback = () => {
				this.isFetching = false;
				this.isAll = true;
			};
			walletStashApi.getList(params, callback, errorCallback);
		},
		init() {
			this.checkCapabilities();
			this.initFilterDate();
			this.fetchHistories();
		},
		setFilterDate(filterDate) {
			this.filterDate = filterDate;
			const since = dayjs(filterDate.start).format('YYYY-MM-DD');
			const until = dayjs(filterDate.end).format('YYYY-MM-DD');
			const query = duplicateVar(this.$route.query);
			query.since = since;
			query.until = until;
			const params = { query };
			this.$router.push(params);
			this.fetchHistories(true);
			this.fetchOverviews();
		},
		onKeywordChange() {
			const self = this;
			this.ooDelay(() => {
				self.fetchHistories();
				self.fetchOverviews();
			}, 1000);
		},
		handleScroll() {
			const pageHeight = document.documentElement.offsetHeight;
			const windowHeight = window.innerHeight;
			const scrollPosition = window.scrollY;
			const offset = 50;
			const isOnBottom = pageHeight - offset <= windowHeight + scrollPosition;
			if (isOnBottom) {
				if (!this.isFetching && !this.isAll) {
					this.fetchHistories(false);
				}
			}
		},
		changeSorting(key) {
			const isSame = this.sort_by === key;
			this.sort_by = key;
			const sort = isSame && this.sort === 'asc' ? 'desc' : 'asc';
			this.sort = sort;
			// Re-fetch
			this.fetchHistories(true);
		},
		sortClass(key) {
			const sameKey = this.sort_by === key;
			const className = sameKey ? `th-sort-${this.sort}` : '';
			return className;
		},
		checkCapabilities() {
			if (!this.isOwner && !this.isUserCan('wallet_view')) {
				this.$router.push('/');
			} else {
				this.isRouteChecked = true;
			}
		},
		formatDate(dateString) {
			const format = 'DD-MM-YYYY - HH:mm';
			const timezone = this.user.timezone;
			const formattedDate = this.moment.utc(dateString).tz(timezone).format(format);
			return dateString && dateString !== '' ? formattedDate : '';
		},
		initFilterDate() {
			if (this.since && this.since !== '' && this.until && this.until !== '') {
				const format = 'YYYY-MM-DD';
				const since = dayjs(this.since).format(format);
				const until = dayjs(this.until).format(format);
				const filterDate = {
					start: since,
					end: until,
				};
				this.filterDate = filterDate;
			}
		},
		getMemberName(id) {
			const member = this.members.find(member => member._id === id);
			return member ? member.name : '';
		},
		formatNumber(number) {
			return formatNumberPlain(number);
		},
		showDetails(history) {
			if (this.selected && this.selected._id === history._id) {
				this.selected = null;
				return;
			}
			this.selected = duplicateVar(history);
		},
		getValueId(history) {
			if (this.activePage === 'handling_fee') {
				return history.handling_fee_id;
			}

			return history.order_id;
		},
	},
	computed: {
		...mapGetters({
			user: 'user',
			isAdmin: 'isAdmin',
			isOwner: 'isOwner',
			isUserCan: 'isUserCan',
			isTester: 'isTester',
			bankAccounts: 'bankAccounts',
			presetRangeLabel: 'presetRangeLabel',
			filterDatePresets: 'filterDatePresets',
			members: 'members/all',
		}),
		since() {
			const since = this.$route.query.since;
			return since;
		},
		until() {
			const until = this.$route.query.until;
			return until;
		},
		fetchParams() {
			const dateFormat = 'YYYY-MM-DD';
			const startDate = dayjs(this.filterDate.start).format(dateFormat);
			const endDate = dayjs(this.filterDate.end).format(dateFormat);
			const params = {
				limit: this.limit,
				sort_by: this.sort_by,
				sort: this.sort,
				page: this.page,
				since: startDate,
				until: endDate,
			};

			const keyword = this.keyword.trim();
			if (keyword && keyword !== '') {
				params.keyword = keyword;
			}

			if (this.activePage) {
				params.type = this.activePage;
			}

			return params;
		},
		pageTitle() {
			if (this.activePage === 'handling_fee') {
				return 'Handling Fee Stash';
			}

			return 'Credit Stash';
		},
		titleId() {
			if (this.activePage === 'handling_fee') {
				return 'Handling Fee ID';
			}

			return 'Order ID';
		},
		activePage() {
			return this.$route.params.type;
		},
	},
	watch: {
		user() {
			this.init();
		},
	},
	created() {
		if (this.user) {
			this.init();
		}
		window.addEventListener('scroll', this.handleScroll);
		document.addEventListener('scroll', this.onDocumentScroll, true);
	},
	destroyed() {
		window.removeEventListener('scroll', this.handleScroll);
		document.removeEventListener('scroll', this.onDocumentScroll, true);
	},
};
</script>
